import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { updateProfile, formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkles, Lock, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { KnownDevices } from "@/components/KnownDevices";

export const TrialBanner = () => {
  const { user, refresh } = useAuth();
  React.useEffect(() => {
    const h = (e) => { toast.error(e.detail, { duration: 8000 }); refresh(); };
    window.addEventListener("d20-readonly", h);
    return () => window.removeEventListener("d20-readonly", h);
  }, [refresh]);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const a = user?.access;
  if (!a || pathname === "/billing") return null;
  if (a.read_only) {
    return (
      <div data-testid="trial-banner" data-state="ended" className="border-b border-red-500/40 bg-red-500/10 px-4 py-2 text-sm flex flex-wrap items-center justify-center gap-3">
        <Lock className="h-4 w-4 text-red-500" /> <span>Your free trial has ended — the workspace is <strong>read-only</strong> until you choose a plan.</span>
        <Button size="sm" data-testid="trial-upgrade-btn" onClick={() => navigate("/billing")}>Choose a plan</Button>
      </div>
    );
  }
  if (!a.on_trial) return null;
  const urgent = a.trial_days_left <= 3;
  return (
    <div data-testid="trial-banner" data-state="trial" className={cn("border-b px-4 py-2 text-sm flex flex-wrap items-center justify-center gap-3", urgent ? "border-amber-500/40 bg-amber-500/10" : "border-primary/30 bg-primary/5")}>
      <Sparkles className={cn("h-4 w-4", urgent ? "text-amber-500" : "text-primary")} />
      <span><strong data-testid="trial-days-left">{a.trial_days_left} day{a.trial_days_left === 1 ? "" : "s"}</strong> left on your Business trial — 1,000 invoices &amp; 5 users included.</span>
      {user.role === "admin" && <Button size="sm" variant={urgent ? "default" : "outline"} data-testid="trial-upgrade-btn" onClick={() => navigate("/billing")}>Upgrade</Button>}
    </div>
  );
};

export const ProfileDialog = ({ open, onOpenChange }) => {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {};
      if (name.trim() && name.trim() !== user.name) body.name = name.trim();
      if (pw) { body.new_password = pw; body.current_password = cur; }
      if (!Object.keys(body).length) { toast.message("Nothing to save"); setBusy(false); return; }
      const u = await updateProfile(body);
      setUser(u);
      toast.success(pw ? "Password updated" : "Profile updated");
      setCur(""); setPw("");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="profile-dialog">
        <DialogHeader><DialogTitle>Your profile</DialogTitle><DialogDescription>{user?.email}</DialogDescription></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div><label className="text-xs text-muted-foreground">Display name</label><Input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} minLength={2} className="mt-1" /></div>
          <div className="rounded-lg border border-border p-3 space-y-2">
            <div className="text-xs font-medium">Change password</div>
            <Input data-testid="profile-current-password" type="password" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} />
            <Input data-testid="profile-new-password" type="password" placeholder="New password (min 8 characters)" minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} />
          </div>
          {open && <KnownDevices />}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" data-testid="profile-save" disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
